import React from "react";
import { Select, SelectItem, SelectProvider } from "../../../form/select";
import { useSelectStore, SelectLabel } from "@ariakit/react";
import { Color } from "../../../../constants/color";
import { ZIndex } from "../../../../constants/z-index";
import { BreakpointsQuery } from "../../../../constants/device";
import styled from "styled-components";
import Textbox from "../../../form/input";
import Button from "../../../button/button";
import Suggerimento from "../../../tooltip/suggerimento";
import { Icons } from "../../../../constants/icons";
import { User } from "../../../../types";
import { useTranslation } from "react-i18next";

interface UserRowProps {
  user: User;
  index: number;
  edit: boolean;
  users: Set<User>;
  editCompanyData: (
    field: string,
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.FormEvent<HTMLButtonElement>
      | undefined,
    index: number,
    users?: Set<User>
  ) => void;
  renderRolesSelectItems: (
    roles: Map<number, { id: number; description: string | null }>
  ) => JSX.Element[];
  roles: Map<number, { id: number; description: string | null }> | null;
  handleDeleteUser: (user: User) => void;
}

const InfoContainer = styled.div<{
  $edit: boolean;
  $isDeleted?: boolean;
}>`
  display: flex;
  flex-direction: row;
  column-gap: 80px;
  margin-top: 30px;
  position: ${(props) => (props.$edit ? "relative" : "unset")};
  right: ${(props) => (props.$edit ? "66px" : "unset")};
  opacity: ${(props) => (props.$isDeleted ? "0.5" : "1")};
  position: relative;

  &:after {
    content: ${(props) => (props.$isDeleted ? "'Utente eliminato'" : "''")};
    display: ${(props) => (props.$isDeleted ? "block" : "none")};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: ${Color.RED};
    color: ${Color.WHITE};
    padding: 5px 10px;
    border-radius: 5px;
    font-weight: bold;
    z-index: ${ZIndex.CONTENT};
  }

  input {
    background-color: ${Color.WHITE};
    box-shadow: none;
    border-style: solid;
    border-width: 2px;
    border-color: ${Color.LIGHT_BLUE};
    border-radius: 20px;
    width: 480px;
    height: 30px;
    @media ${BreakpointsQuery.tablet} {
      width: 400px;
    }
  }
  input:focus {
    outline-width: 0;
  }
  @media ${BreakpointsQuery.tablet} {
    column-gap: 20px;
    right: ${(props) => (props.$edit ? "35px" : "unset")};
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 30px;
`;

const RemoveButtonContainer = styled.div<{ $edit: boolean }>`
  display: ${(props) => (props.$edit ? "unset" : "none")};
  margin-top: 24px;
  width: min-content;
  position: relative;
  left: 65px;
  @media ${BreakpointsQuery.tablet} {
    left: 5px;
  }
`;

const selectStyle = {
  "--padBlock": "0.3rem",
  border: "2px solid",
  borderRadius: "1rem",
  fontSize: "1rem",
  borderColor: Color.LIGHT_BLUE,
} as React.CSSProperties;

const UserRow: React.FC<UserRowProps> = ({
  user,
  index,
  edit,
  users,
  editCompanyData,
  renderRolesSelectItems,
  roles,
  handleDeleteUser,
}) => {
  const { t } = useTranslation();
  const roleStore = useSelectStore({
    value: String(user.role),
    setValue: (newValue) => {
      // Creiamo un mock di evento per aggiornare il ruolo
      editCompanyData(
        "admins.role",
        {
          currentTarget: { value: newValue },
          target: { value: newValue },
        } as React.ChangeEvent<HTMLInputElement>,
        index,
        users
      );
    },
  });

  return (
    <InfoContainer key={index} $edit={edit} $isDeleted={user.isDeleted}>
      <RemoveButtonContainer $edit={edit}>
        <Suggerimento testo={t("user-aiuto-rimuovi-utente")}>
          <Button
            color={Color.RED}
            textColor={Color.WHITE}
            aria-label={t("user-rimuovi-utente", {
              utente:
                `${user.lastName ?? ""} ${user.firstName ?? ""}`.trim() ||
                user.email,
            })}
            icon={Icons.BUTTONS.CANCEL}
            onClick={() => handleDeleteUser(user)}
          />
        </Suggerimento>
      </RemoveButtonContainer>
      <Column>
        <Textbox
          label={t("reserved-courses-detail-header-first-name")}
          alt={t("reserved-courses-detail-header-first-name")}
          autocomplete={"off"}
          form={"company-info"}
          name={t("reserved-courses-detail-header-first-name")}
          defaultValue={user.firstName}
          type={"text"}
          textAlign={"left"}
          onChange={(e) => {
            editCompanyData(`admins.firstName`, e, index, users);
          }}
          required={true}
          edit={edit}
        />

        <Textbox
          label={t("reset-password-email")}
          alt={t("reset-password-email")}
          autocomplete={"off"}
          form={"company-info"}
          name={t("reset-password-email")}
          defaultValue={user.email}
          type={"text"}
          textAlign={"left"}
          onChange={(e) => {
            editCompanyData(`admins.email`, e, index, users);
          }}
          required={true}
          edit={edit}
        />
        <SelectProvider store={roleStore}>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}
          >
            <SelectLabel
              style={{
                paddingLeft: "1.2rem",
                fontSize: "14px",
                color: Color.LIGHT_BLUE,
                fontFamily: "Avenir Next, sans-serif",
                fontWeight: "bold",
              }}
            >
              {t("registration-role")}
            </SelectLabel>
            <Select
              style={selectStyle}
              sameWidth={true}
              required={true}
              renderValue={(value) => {
                if (!value) return "Seleziona un ruolo...";
                return (
                  roles?.get(+value)?.description ?? "Seleziona un ruolo..."
                );
              }}
            >
              {roles ? renderRolesSelectItems(roles) : null}
            </Select>
          </div>
        </SelectProvider>
      </Column>
      <Column>
        <Textbox
          label={t("reserved-courses-detail-header-last-name")}
          alt={t("reserved-courses-detail-header-last-name")}
          autocomplete={"off"}
          form={"company-info"}
          name={t("reserved-courses-detail-header-last-name")}
          defaultValue={user.lastName}
          type={"text"}
          textAlign={"left"}
          onChange={(e) => {
            editCompanyData(`admins.lastName`, e, index, users);
          }}
          required={true}
          edit={edit}
        />
        <Textbox
          label={t("login-username")}
          alt={t("login-username")}
          autocomplete={"off"}
          form={"company-info"}
          name={t("login-username")}
          defaultValue={user.username}
          type={"text"}
          textAlign={"left"}
          onChange={(e) => {
            editCompanyData(`admins.username`, e, index, users);
          }}
          required={true}
          edit={edit}
        />
      </Column>
    </InfoContainer>
  );
};

export default UserRow;
